/**
 * Yönetim → Kataloglar sekmesi: Bilişsel Duruşlar + Domain Nesneleri.
 *
 * Bu iki CRUD daha önce \/settings\ (Ayarlar) sayfasının dibinde duruyordu
 * ama \/admin/cognitive-dispositions\ ve \/admin/domain-objects * endpointlerine gidiyorlar; ikisi de equire_admin\ istiyor. Sonuç:
 * admin olmayan bir kullanıcı Ayarlar'ı açtığında listeler 403 alıp SESSİZCE
 * boş kalıyordu (2026-07-25 ayar denetimi bulgusu). Yetki seviyesi neyse
 * yüzey de orada durmalı — bu yüzden Yönetim'e taşındılar.
 *
 * Kapsam GLOBAL: buradaki kayıtlar kullanıcıya özel değil, tüm sistemi
 * besleyen katalog verisidir.
 */
import { useState, useEffect, useCallback } from "react";
import type { CognitiveDisposition, DomainObject } from "../../transport/authClient";
import {
  adminListDispositions,
  adminCreateDisposition,
  adminUpdateDisposition,
  adminDeleteDisposition,
  adminListDomainObjects,
  adminCreateDomainObject,
  adminUpdateDomainObject,
  adminDeleteDomainObject,
} from "../../transport/authClient";
import { Eye, Crosshair, Shield, Flame, Swords, Scale, Plus, Pencil, Trash2, Check, X } from "lucide-react";

export function CatalogsTab() {
  return (
    <div className="space-y-6">
      <CognitiveDispositionsSection />
      <DomainObjectsSection />
    </div>
  );
}

/* ─── Cognitive Dispositions Admin ─────────────────────────────────────── */

// Lucide icon refs are typed as ForwardRefExotic with optional `className`,
// which trips `exactOptionalPropertyTypes`. We narrow to a permissive
// component type that accepts any className/string-only prop subset.
type IconCmp = React.ComponentType<{ className?: string }>;
const ICON_MAP: Record<string, IconCmp> = {
  eye: Eye as unknown as IconCmp,
  crosshair: Crosshair as unknown as IconCmp,
  shield: Shield as unknown as IconCmp,
  flame: Flame as unknown as IconCmp,
  swords: Swords as unknown as IconCmp,
  scale: Scale as unknown as IconCmp,
};

function DispositionIcon({ name, className }: { name: string; className?: string }) {
  const Ic = ICON_MAP[name] ?? (Shield as unknown as IconCmp);
  return <Ic {...(className ? { className } : {})} />;
}

function CognitiveDispositionsSection() {
  const [items, setItems] = useState<CognitiveDisposition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Partial<CognitiveDisposition>>({});
  const [opError, setOpError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    adminListDispositions()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function startEdit(d: CognitiveDisposition) {
    setEditing(d.id);
    setForm({ ...d });
    setCreating(false);
    setOpError(null);
  }

  function startCreate() {
    setCreating(true);
    setEditing(null);
    setForm({
      slug: "",
      label_tr: "",
      description: "",
      icon_name: "shield",
      prompt_template: "",
      tool_affinity: 0.5,
      sort_order: items.length + 1,
      is_active: true,
    });
    setOpError(null);
  }

  async function saveEdit() {
    if (!editing) return;
    try {
      await adminUpdateDisposition(editing, form);
      setEditing(null);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Kaydetme hatası");
    }
  }

  async function saveCreate() {
    try {
      await adminCreateDisposition(form as Omit<CognitiveDisposition, "id">);
      setCreating(false);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Ekleme hatası");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Bu bilişsel duruşu silmek istediğinize emin misiniz?")) return;
    try {
      await adminDeleteDisposition(id);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Silme hatası");
    }
  }

  return (
    <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold">Bilişsel Duruşlar (Cognitive Dispositions)</h2>
        <button
          onClick={startCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded-lg hover:bg-indigo-500/20 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Yeni Ekle
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Konsey üyelerinin tartışmadaki müdahale tarzlarını belirleyen bilişsel duruşları yönetin.
      </p>

      {opError && (
        <div className="mb-3 px-3 py-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg">
          {opError}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-500 py-4 text-center">Yükleniyor...</p>
      ) : (
        <div className="space-y-2">
          {creating && (
            <DispositionRow
              form={form}
              setForm={setForm}
              onSave={saveCreate}
              onCancel={() => setCreating(false)}
              isNew
            />
          )}
          {items.map((d) =>
            editing === d.id ? (
              <DispositionRow
                key={d.id}
                form={form}
                setForm={setForm}
                onSave={saveEdit}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div
                key={d.id}
                className="flex items-center gap-3 px-4 py-3 bg-white/[0.02] border border-white/5 rounded-xl group hover:border-white/10 transition-colors"
              >
                <DispositionIcon name={d.icon_name} className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-200">{d.label_tr}</span>
                    <span className="text-[10px] font-mono text-slate-600 bg-white/5 px-1.5 py-0.5 rounded">{d.slug}</span>
                    {!d.is_active && (
                      <span className="text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">Pasif</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{d.description}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(d)}
                    className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-lg hover:bg-white/5 transition-colors"
                    title="Düzenle"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(d.id)}
                    className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-white/5 transition-colors"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ),
          )}
          {!items.length && !creating && (
            <p className="text-xs text-slate-500 py-4 text-center">Henüz bilişsel duruş tanımlanmamış.</p>
          )}
        </div>
      )}
    </section>
  );
}

function DispositionRow({
  form,
  setForm,
  onSave,
  onCancel,
  isNew,
}: {
  form: Partial<CognitiveDisposition>;
  setForm: (f: Partial<CognitiveDisposition>) => void;
  onSave: () => void;
  onCancel: () => void;
  isNew?: boolean;
}) {
  return (
    <div className="px-4 py-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {isNew && (
          <input
            value={form.slug ?? ""}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
            placeholder="slug (ör. cost-analyst)"
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
          />
        )}
        <input
          value={form.label_tr ?? ""}
          onChange={(e) => setForm({ ...form, label_tr: e.target.value })}
          placeholder="Türkçe etiket"
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        />
        <select
          value={form.icon_name ?? "shield"}
          onChange={(e) => setForm({ ...form, icon_name: e.target.value })}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        >
          {Object.keys(ICON_MAP).map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>
      <input
        value={form.description ?? ""}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Kısa açıklama"
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
      />
      <textarea
        value={form.prompt_template ?? ""}
        onChange={(e) => setForm({ ...form, prompt_template: e.target.value })}
        placeholder="Sistem talimatı şablonu"
        rows={3}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 resize-y"
      />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-400">
          Araç Eğilimi:
          <input
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={form.tool_affinity ?? 0.5}
            onChange={(e) => setForm({ ...form, tool_affinity: parseFloat(e.target.value) })}
            className="w-20 h-1 accent-indigo-500"
          />
          <span className="font-mono text-indigo-400">{(form.tool_affinity ?? 0.5).toFixed(1)}</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={form.is_active ?? true}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="accent-indigo-500"
          />
          Aktif
        </label>
        <div className="flex-1" />
        <button onClick={onCancel} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
          <X className="w-4 h-4" />
        </button>
        <button onClick={onSave} className="p-1.5 text-emerald-400 hover:text-emerald-300 rounded-lg hover:bg-emerald-500/10">
          <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── Domain Objects Admin ─────────────────────────────────────────────── */

const DOMAIN_CATEGORIES = [
  "Hava Platformları",
  "İHA Sistemleri",
  "Kara Platformları",
  "Deniz Platformları",
  "Hava Savunma",
  "Mühimmat",
  "Elektronik Harp",
  "C4ISR",
  "Lojistik",
  "Diğer",
];

function DomainObjectsSection() {
  const [items, setItems] = useState<DomainObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Partial<DomainObject>>({});
  const [opError, setOpError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    adminListDomainObjects()
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function startEdit(d: DomainObject) {
    setEditing(d.id);
    setForm({ ...d });
    setCreating(false);
    setOpError(null);
  }

  function startCreate() {
    setCreating(true);
    setEditing(null);
    setForm({
      name: "",
      category: DOMAIN_CATEGORIES[0] ?? "Diğer",
      description: "",
      specs: {},
      icon_name: "box",
      sort_order: items.length + 1,
      is_active: true,
    });
    setOpError(null);
  }

  async function saveEdit() {
    if (!editing) return;
    try {
      await adminUpdateDomainObject(editing, form);
      setEditing(null);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Kaydetme hatası");
    }
  }

  async function saveCreate() {
    try {
      await adminCreateDomainObject(form as Omit<DomainObject, "id">);
      setCreating(false);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Ekleme hatası");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Bu domain nesnesini silmek istediğinize emin misiniz?")) return;
    try {
      await adminDeleteDomainObject(id);
      refresh();
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Silme hatası");
    }
  }

  const grouped = items.reduce<Record<string, DomainObject[]>>((acc, d) => {
    (acc[d.category] ??= []).push(d);
    return acc;
  }, {});

  return (
    <section className="bg-white/[0.02] border border-white/5 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-base font-semibold">Savunma Domain Nesneleri</h2>
        <button
          onClick={startCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded-lg hover:bg-indigo-500/20 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Yeni Ekle
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Tartışmalarda referans olarak kullanılabilecek savunma platformları ve sistemleri.
      </p>

      {opError && (
        <div className="mb-3 px-3 py-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg">
          {opError}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-500 py-4 text-center">Yükleniyor...</p>
      ) : (
        <div className="space-y-4">
          {creating && (
            <DomainObjectRow
              form={form}
              setForm={setForm}
              onSave={saveCreate}
              onCancel={() => setCreating(false)}
              isNew
            />
          )}
          {Object.entries(grouped).map(([cat, objs]) => (
            <div key={cat}>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{cat}</h3>
              <div className="space-y-1.5">
                {objs.map((d) =>
                  editing === d.id ? (
                    <DomainObjectRow
                      key={d.id}
                      form={form}
                      setForm={setForm}
                      onSave={saveEdit}
                      onCancel={() => setEditing(null)}
                    />
                  ) : (
                    <div
                      key={d.id}
                      className="flex items-center gap-3 px-4 py-2.5 bg-white/[0.02] border border-white/5 rounded-xl group hover:border-white/10 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-200">{d.name}</span>
                          {!d.is_active && (
                            <span className="text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">Pasif</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 truncate mt-0.5">{d.description}</p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => startEdit(d)}
                          className="p-1.5 text-slate-400 hover:text-indigo-400 rounded-lg hover:bg-white/5"
                          title="Düzenle"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(d.id)}
                          className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-white/5"
                          title="Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          ))}
          {!items.length && !creating && (
            <p className="text-xs text-slate-500 py-4 text-center">Henüz domain nesnesi tanımlanmamış.</p>
          )}
        </div>
      )}
    </section>
  );
}

function DomainObjectRow({
  form,
  setForm,
  onSave,
  onCancel,
}: {
  form: Partial<DomainObject>;
  setForm: (f: Partial<DomainObject>) => void;
  onSave: () => void;
  onCancel: () => void;
  isNew?: boolean;
}) {
  return (
    <div className="px-4 py-3 bg-indigo-500/5 border border-indigo-500/20 rounded-xl space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <input
          value={form.name ?? ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Platform / Sistem adı"
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        />
        <select
          value={form.category ?? DOMAIN_CATEGORIES[0]}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        >
          {DOMAIN_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <textarea
        value={form.description ?? ""}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Teknik açıklama (sensör suite, silah entegrasyonu, performans parametreleri...)"
        rows={2}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 resize-y"
      />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={form.is_active ?? true}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="accent-indigo-500"
          />
          Aktif
        </label>
        <div className="flex-1" />
        <button onClick={onCancel} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
          <X className="w-4 h-4" />
        </button>
        <button onClick={onSave} className="p-1.5 text-emerald-400 hover:text-emerald-300 rounded-lg hover:bg-emerald-500/10">
          <Check className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
